/**
 * The one capture this device is running, as a tiny `useSyncExternalStore`
 * store: fed by `recorder.subscribe` + `recorder.status()`, persisted per user
 * in localStorage (`{ recordingId, sessionUuid, userId }`) so the REC bar
 * survives reloads, plus the capture actions (pause / resume / finish /
 * withdraw / discard) the REC bar and the consent sheet call.
 *
 * Capture events go to the API best-effort through a small retry queue — the
 * UI never waits on them. The consent-critical ones (someone-joined addendum,
 * stopped, withdraw, discard) go through the persisted outbox instead, so an
 * app kill or a long time offline cannot lose them. Nothing here carries audio
 * or transcript.
 */

import { useSyncExternalStore } from 'react'
import {
  SESSION_RECORDING_BUCKET_EXPIRY_DAYS,
  SESSION_RECORDING_MAX_SECONDS,
  type SessionRecordingConsentInput,
  type SessionRecordingEventInput,
  type SessionRecordingEventReason,
  type SessionRecordingPersonPresent,
  type SessionRecordingSummary,
  type SessionRecordingWithdrawnBy,
} from '@shared/sessions/session-recording'
import { getSessionAudioRecorder, getSessionRecordingUploader } from '../data/adapters'
import {
  IDLE_RECORDER_SNAPSHOT,
  type OrphanRecording,
  type RecorderEvent,
  type RecorderPauseReason,
  type RecorderSnapshot,
  type SessionAudioRecorder,
} from '../data/recorder-port'
import { SecureStorage } from '../../../services/secure-storage'
import { createSessionRecordingOutbox, type OutboxCall } from '../data/session-recording-outbox'
import {
  createSessionRecording,
  discardSessionRecording,
  postSessionRecordingEvent,
  withdrawSessionRecording,
} from '../data/session-recordings-api'
import { isApiErrorRetryable } from '../utils/recording-labels'
import { createRetryQueue } from '../utils/retry-queue'

export interface ActiveRecordingRef {
  recordingId: string
  sessionUuid: string
  userId: string
}

export type ActiveRecordingBusy = 'pausing' | 'resuming' | 'finishing' | 'withdrawing' | 'discarding' | null

export interface ActiveRecordingState {
  /** The capture this device owns for the signed-in user (null when none / another user's). */
  ref: ActiveRecordingRef | null
  snapshot: RecorderSnapshot
  /** `Date.now()` when {@link snapshot} arrived — the timer extrapolates from it. */
  snapshotAtMs: number
  busy: ActiveRecordingBusy
  /** Latest recorder error code (shown as a generic alert, never raw). */
  recorderError: string | null
}

export interface RecorderLevelState {
  level: number
  /** Last time the mic heard something while capturing (silence warning). */
  lastAudibleAtMs: number
}

const ACTIVE_KEY = 'ascend-session-recording-active'
const SESSIONS_KEY_PREFIX = 'ascend-session-recording-sessions:'
/** Below this the meter counts as silence. */
export const SILENCE_LEVEL_THRESHOLD = 0.02
export const SILENCE_WARNING_MS = 2 * 60 * 1000

const INITIAL_STATE: ActiveRecordingState = {
  ref: null,
  snapshot: IDLE_RECORDER_SNAPSHOT,
  snapshotAtMs: 0,
  busy: null,
  recorderError: null,
}

let state: ActiveRecordingState = INITIAL_STATE
let levelState: RecorderLevelState = { level: 0, lastAudibleAtMs: 0 }
const listeners = new Set<() => void>()
const levelListeners = new Set<() => void>()
let boundUserId: string | null = null
let unbindRecorder: (() => void) | null = null
let uiResumeInFlight = false
let capFinishHandler: ((result: FinishResult) => void) | null = null

const eventQueue = createRetryQueue({ isRetryable: isApiErrorRetryable })

function sendOutboxCall(call: OutboxCall): Promise<unknown> {
  if (call.kind === 'event') return postSessionRecordingEvent(call.sessionUuid, call.recordingId, call.body)
  if (call.kind === 'withdraw') return withdrawSessionRecording(call.sessionUuid, call.recordingId, { by: call.by })
  return discardSessionRecording(call.sessionUuid, call.recordingId)
}

const outbox = createSessionRecordingOutbox({
  storage: SecureStorage,
  send: sendOutboxCall,
  isRetryable: isApiErrorRetryable,
})

const flushOutbox = () => outbox.flush()
const flushOutboxWhenVisible = () => {
  if (document.visibilityState === 'visible') outbox.flush()
}

function setState(patch: Partial<ActiveRecordingState>): void {
  state = { ...state, ...patch }
  listeners.forEach((listener) => listener())
}

function setLevel(next: RecorderLevelState): void {
  levelState = next
  levelListeners.forEach((listener) => listener())
}

// --- persistence -------------------------------------------------------------

function readActiveRef(): ActiveRecordingRef | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ActiveRecordingRef>
    if (
      typeof parsed.recordingId === 'string' &&
      typeof parsed.sessionUuid === 'string' &&
      typeof parsed.userId === 'string'
    ) {
      return { recordingId: parsed.recordingId, sessionUuid: parsed.sessionUuid, userId: parsed.userId }
    }
  } catch {
    /* unreadable → none */
  }
  return null
}

function writeActiveRef(ref: ActiveRecordingRef | null): void {
  try {
    if (ref) localStorage.setItem(ACTIVE_KEY, JSON.stringify(ref))
    else localStorage.removeItem(ACTIVE_KEY)
  } catch {
    /* private mode / quota */
  }
}

type SessionsMap = Record<string, { sessionUuid: string; at: number }>

function readSessionsMap(userId: string): SessionsMap {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY_PREFIX + userId)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    return parsed && typeof parsed === 'object' ? (parsed as SessionsMap) : {}
  } catch {
    return {}
  }
}

/** Remember which session a recording belongs to (orphans only carry the recording id). */
function rememberRecordingSession(userId: string, recordingId: string, sessionUuid: string): void {
  const cutoff = Date.now() - SESSION_RECORDING_BUCKET_EXPIRY_DAYS * 24 * 60 * 60 * 1000
  const map = readSessionsMap(userId)
  const next: SessionsMap = {}
  for (const [id, entry] of Object.entries(map)) {
    if (entry && typeof entry.sessionUuid === 'string' && entry.at > cutoff) next[id] = entry
  }
  next[recordingId] = { sessionUuid, at: Date.now() }
  try {
    localStorage.setItem(SESSIONS_KEY_PREFIX + userId, JSON.stringify(next))
  } catch {
    /* ignore */
  }
}

function forgetRecordingSession(userId: string, recordingId: string): void {
  const map = readSessionsMap(userId)
  if (!(recordingId in map)) return
  delete map[recordingId]
  try {
    localStorage.setItem(SESSIONS_KEY_PREFIX + userId, JSON.stringify(map))
  } catch {
    /* ignore */
  }
}

/** The session a recording on this device belongs to, for the signed-in user. */
export function sessionUuidForRecording(recordingId: string): string | null {
  if (!boundUserId) return null
  return readSessionsMap(boundUserId)[recordingId]?.sessionUuid ?? null
}

function setRef(ref: ActiveRecordingRef | null): void {
  writeActiveRef(ref)
  setState({ ref })
}

// --- recorder binding ----------------------------------------------------------

export function isCaptureLive(snapshot: RecorderSnapshot): boolean {
  return snapshot.state === 'recording' || snapshot.state === 'paused' || snapshot.state === 'starting'
}

/** Audio seconds captured, extrapolated between recorder snapshots while recording. */
export function liveElapsedSec(current: ActiveRecordingState, nowMs: number): number {
  const base = current.snapshot.elapsedAudioSec
  if (current.snapshot.state !== 'recording' || current.snapshotAtMs <= 0) return base
  return Math.min(SESSION_RECORDING_MAX_SECONDS, base + Math.max(0, nowMs - current.snapshotAtMs) / 1000)
}

function applySnapshot(snapshot: RecorderSnapshot): void {
  const now = Date.now()
  let ref = state.ref
  if (isCaptureLive(snapshot) && snapshot.recordingId && boundUserId) {
    if (!ref || ref.recordingId !== snapshot.recordingId) {
      const sessionUuid = readSessionsMap(boundUserId)[snapshot.recordingId]?.sessionUuid
      ref = sessionUuid ? { recordingId: snapshot.recordingId, sessionUuid, userId: boundUserId } : null
      writeActiveRef(ref)
    }
  } else if (snapshot.state === 'idle' && ref && state.busy == null) {
    // Nothing is capturing: an app kill leaves an orphan (`findOrphans`), not a live bar.
    ref = null
    writeActiveRef(null)
  }
  const resumedCapture = snapshot.state === 'recording' && state.snapshot.state !== 'recording'
  setState({ snapshot, snapshotAtMs: now, ref })
  if (resumedCapture) setLevel({ level: levelState.level, lastAudibleAtMs: now })
}

function eventAt(atMs?: number): string {
  return new Date(atMs ?? Date.now()).toISOString()
}

/**
 * Capture event, never awaited by the UI. Best-effort by default; `critical`
 * events (consent addendum, stopped) go through the persisted outbox.
 */
function postEvent(
  ref: ActiveRecordingRef | null,
  event: Omit<SessionRecordingEventInput, 'at'> & { atMs?: number },
  options?: { critical?: boolean },
): void {
  if (!ref) return
  const { atMs, ...rest } = event
  const body: SessionRecordingEventInput = {
    ...rest,
    at: eventAt(atMs),
    elapsedSec: Math.max(0, Math.floor(rest.elapsedSec)),
  }
  if (options?.critical) {
    void outbox.push({ kind: 'event', sessionUuid: ref.sessionUuid, recordingId: ref.recordingId, body })
    return
  }
  eventQueue.push({
    key: `${ref.recordingId}:${body.type}`,
    run: () => postSessionRecordingEvent(ref.sessionUuid, ref.recordingId, body),
  })
}

function onRecorderEvent(event: RecorderEvent): void {
  switch (event.type) {
    case 'state':
      applySnapshot(event.snapshot)
      return
    case 'level': {
      const now = Date.now()
      const audible = event.level > SILENCE_LEVEL_THRESHOLD
      setLevel({ level: event.level, lastAudibleAtMs: audible ? now : levelState.lastAudibleAtMs })
      return
    }
    case 'interrupted':
      postEvent(state.ref, {
        type: 'interrupted',
        atMs: event.atMs,
        elapsedSec: liveElapsedSec(state, event.atMs),
        reason: event.reason,
      })
      return
    case 'resumed':
      // Resumes the provider asked for are posted by `resumeActiveRecording`.
      if (!uiResumeInFlight) {
        postEvent(state.ref, { type: 'resumed', atMs: event.atMs, elapsedSec: state.snapshot.elapsedAudioSec })
      }
      return
    case 'capReached': {
      if (state.busy === 'finishing' || state.busy === 'withdrawing' || state.busy === 'discarding') return
      postEvent(state.ref, {
        type: 'cap_reached',
        atMs: event.atMs,
        elapsedSec: SESSION_RECORDING_MAX_SECONDS,
      })
      void finishActiveRecording({ cause: 'cap' }).then(
        (result) => capFinishHandler?.(result),
        () => undefined,
      )
      return
    }
    case 'error':
      setState({ recorderError: event.code })
      return
  }
}

/**
 * Bind the store to the signed-in user + device recorder (the global REC bar
 * controller does this once). Returns the unbind.
 */
export function bindActiveSessionRecording(userId: string, recorder: SessionAudioRecorder): () => void {
  unbindRecorder?.()
  boundUserId = userId
  const persisted = readActiveRef()
  state = { ...INITIAL_STATE, ref: persisted && persisted.userId === userId ? persisted : null }
  listeners.forEach((listener) => listener())
  void outbox.bind(userId)
  window.addEventListener('online', flushOutbox)
  document.addEventListener('visibilitychange', flushOutboxWhenVisible)
  const unsubscribe = recorder.subscribe(onRecorderEvent)
  let cancelled = false
  void recorder.status().then(
    (snapshot) => {
      if (!cancelled) applySnapshot(snapshot)
    },
    () => undefined,
  )
  const unbind = () => {
    cancelled = true
    unsubscribe()
    window.removeEventListener('online', flushOutbox)
    document.removeEventListener('visibilitychange', flushOutboxWhenVisible)
    outbox.unbind()
    if (unbindRecorder === unbind) unbindRecorder = null
    boundUserId = null
    state = INITIAL_STATE
    listeners.forEach((listener) => listener())
  }
  unbindRecorder = unbind
  return unbind
}

/** Called with the result when the 100-minute cap auto-finishes (REC bar toast). */
export function setCapFinishHandler(handler: ((result: FinishResult) => void) | null): void {
  capFinishHandler = handler
}

// --- actions ---------------------------------------------------------------------

/** Create the recording with the consent, then start capture; undoes the create if start fails. */
export async function startSessionRecordingCapture(input: {
  sessionUuid: string
  userId: string
  consent: SessionRecordingConsentInput
}): Promise<SessionRecordingSummary> {
  const recorder = getSessionAudioRecorder()
  const { recording } = await createSessionRecording(input.sessionUuid, { consent: input.consent })
  rememberRecordingSession(input.userId, recording.recordingId, input.sessionUuid)
  try {
    await recorder.start({ recordingId: recording.recordingId, maxSeconds: SESSION_RECORDING_MAX_SECONDS })
  } catch (err) {
    forgetRecordingSession(input.userId, recording.recordingId)
    await deleteOnServer({ kind: 'discard', sessionUuid: input.sessionUuid, recordingId: recording.recordingId })
    throw err
  }
  setRef({ recordingId: recording.recordingId, sessionUuid: input.sessionUuid, userId: input.userId })
  setLevel({ level: 0, lastAudibleAtMs: Date.now() })
  return recording
}

async function withBusy<T>(busy: Exclude<ActiveRecordingBusy, null>, run: () => Promise<T>): Promise<T> {
  setState({ busy })
  try {
    return await run()
  } finally {
    setState({ busy: null })
  }
}

export function pauseActiveRecording(reason: RecorderPauseReason): Promise<void> {
  return withBusy('pausing', async () => {
    const ref = state.ref
    const elapsedSec = liveElapsedSec(state, Date.now())
    await getSessionAudioRecorder().pause(reason)
    postEvent(ref, { type: 'paused', elapsedSec, reason })
  })
}

export function resumeActiveRecording(options?: { personJoined?: SessionRecordingPersonPresent }): Promise<void> {
  return withBusy('resuming', async () => {
    const ref = state.ref
    const { snapshot } = state
    const reason: SessionRecordingEventReason | undefined = options?.personJoined
      ? 'someone_joined'
      : (snapshot.interruption ?? snapshot.pauseReason ?? undefined)
    uiResumeInFlight = true
    try {
      await getSessionAudioRecorder().resume()
    } finally {
      uiResumeInFlight = false
    }
    postEvent(
      ref,
      {
        type: 'resumed',
        elapsedSec: snapshot.elapsedAudioSec,
        ...(reason ? { reason } : {}),
        ...(options?.personJoined ? { personJoined: options.personJoined } : {}),
      },
      // The joined person's consent must reach the server record even after a long offline spell.
      { critical: Boolean(options?.personJoined) },
    )
    setLevel({ level: levelState.level, lastAudibleAtMs: Date.now() })
  })
}

export interface FinishResult {
  recordingId: string
  sessionUuid: string | null
  durationSec: number
  /** False when the upload queue refused the recording (it stays on the phone). */
  uploadQueued: boolean
}

/**
 * Finish: stop capture → `stopped` event → hand the file to the upload queue.
 * Also used for the cap auto-finish and for a capture the device stopped on
 * its own (then `stop()` may fail and the last snapshot supplies the length).
 */
export function finishActiveRecording(options?: { cause?: 'finished' | 'cap' }): Promise<FinishResult> {
  return withBusy('finishing', async () => {
    const ref = state.ref
    const { snapshot } = state
    const recorder = getSessionAudioRecorder()
    let recordingId = ref?.recordingId ?? snapshot.recordingId
    let durationSec = snapshot.elapsedAudioSec
    try {
      const result = await recorder.stop()
      recordingId = result.recordingId
      durationSec = result.durationSec
    } catch (err) {
      const alreadyStopped = options?.cause === 'cap' || snapshot.state === 'stopped'
      if (!alreadyStopped || !recordingId) throw err
    }
    if (!recordingId) throw new Error('No recording to finish')
    postEvent(ref, { type: 'stopped', elapsedSec: durationSec, reason: 'finished' }, { critical: true })
    let uploadQueued = false
    if (ref) {
      try {
        await getSessionRecordingUploader().enqueue({
          recordingId,
          sessionUuid: ref.sessionUuid,
          durationSec: Math.round(durationSec),
        })
        uploadQueued = true
      } catch {
        uploadQueued = false
      }
    }
    setRef(null)
    return { recordingId, sessionUuid: ref?.sessionUuid ?? null, durationSec, uploadQueued }
  })
}

async function stopAndDeleteLocal(recordingId: string | null): Promise<void> {
  const recorder = getSessionAudioRecorder()
  if (isCaptureLive(state.snapshot) || state.snapshot.state === 'stopped') {
    await recorder.stop().catch(() => undefined)
  }
  if (!recordingId) return
  await recorder.discardLocal(recordingId).catch(() => undefined)
  await getSessionRecordingUploader()
    .cancel(recordingId)
    .catch(() => undefined)
}

export interface DeleteResult {
  /** False when the server call failed; it is retried in the background. */
  serverConfirmed: boolean
}

/** Try now; a retryable failure is saved to the outbox and finished later (offline, app killed). */
async function deleteOnServer(call: Extract<OutboxCall, { kind: 'withdraw' | 'discard' }>): Promise<DeleteResult> {
  try {
    await sendOutboxCall(call)
    return { serverConfirmed: true }
  } catch (err) {
    if (isApiErrorRetryable(err)) await outbox.push(call)
    return { serverConfirmed: false }
  }
}

/** Consent withdrawn: stop now, delete the local audio and the upload, tell the server. */
export function withdrawActiveRecording(by: SessionRecordingWithdrawnBy): Promise<DeleteResult> {
  return withBusy('withdrawing', async () => {
    const ref = state.ref
    const recordingId = ref?.recordingId ?? state.snapshot.recordingId
    await stopAndDeleteLocal(recordingId)
    setRef(null)
    if (!ref) return { serverConfirmed: false }
    forgetRecordingSession(ref.userId, ref.recordingId)
    return deleteOnServer({ kind: 'withdraw', sessionUuid: ref.sessionUuid, recordingId: ref.recordingId, by })
  })
}

/** The provider discards the capture: stop, delete locally, delete on the server. */
export function discardActiveRecording(): Promise<DeleteResult> {
  return withBusy('discarding', async () => {
    const ref = state.ref
    const recordingId = ref?.recordingId ?? state.snapshot.recordingId
    await stopAndDeleteLocal(recordingId)
    setRef(null)
    if (!ref) return { serverConfirmed: false }
    forgetRecordingSession(ref.userId, ref.recordingId)
    return deleteOnServer({ kind: 'discard', sessionUuid: ref.sessionUuid, recordingId: ref.recordingId })
  })
}

/** "Finish & create note" for a capture an app kill left on the device. */
export async function finishOrphanRecording(orphan: OrphanRecording, sessionUuid: string): Promise<void> {
  const ref = boundUserId ? { recordingId: orphan.recordingId, sessionUuid, userId: boundUserId } : null
  postEvent(ref, { type: 'stopped', elapsedSec: orphan.elapsedAudioSec, reason: 'app_restarted' }, { critical: true })
  await getSessionRecordingUploader().enqueue({
    recordingId: orphan.recordingId,
    sessionUuid,
    durationSec: Math.round(orphan.elapsedAudioSec),
  })
}

export async function discardOrphanRecording(orphan: OrphanRecording, sessionUuid: string | null): Promise<void> {
  await getSessionAudioRecorder().discardLocal(orphan.recordingId)
  await getSessionRecordingUploader()
    .cancel(orphan.recordingId)
    .catch(() => undefined)
  if (!sessionUuid) return
  if (boundUserId) forgetRecordingSession(boundUserId, orphan.recordingId)
  await deleteOnServer({ kind: 'discard', sessionUuid, recordingId: orphan.recordingId })
}

// --- hooks -------------------------------------------------------------------------

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function subscribeLevel(listener: () => void): () => void {
  levelListeners.add(listener)
  return () => levelListeners.delete(listener)
}

export function getActiveSessionRecordingState(): ActiveRecordingState {
  return state
}

export function useActiveSessionRecording(): ActiveRecordingState {
  return useSyncExternalStore(subscribe, getActiveSessionRecordingState, getActiveSessionRecordingState)
}

const getLevel = () => levelState

/** Mic level (~4 Hz) — separate so the card doesn't re-render with the meter. */
export function useRecorderLevel(): RecorderLevelState {
  return useSyncExternalStore(subscribeLevel, getLevel, getLevel)
}

/** Test helper. */
export function resetActiveSessionRecordingForTests(): void {
  unbindRecorder?.()
  unbindRecorder = null
  boundUserId = null
  uiResumeInFlight = false
  capFinishHandler = null
  eventQueue.clear()
  outbox.unbind()
  state = INITIAL_STATE
  levelState = { level: 0, lastAudibleAtMs: 0 }
  try {
    localStorage.removeItem(ACTIVE_KEY)
  } catch {
    /* ignore */
  }
}

/** Test helper: pending best-effort calls plus saved consent-critical calls. */
export function pendingSessionRecordingEventsForTests(): number {
  return eventQueue.size() + outbox.size()
}
